import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import { useUser } from "./UserContext";
import { CurrencyContext } from "./context/CurrencyContext";
import { api } from "./axiosClient";

const CheckoutPage = () => {
  const { cart, getDeliveryFee } = useContext(CartContext); // ✅ using centralized delivery logic
  const { user } = useUser();
  const { currency, rates, loading } = useContext(CurrencyContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    zip: "",
    city: "",
    state: "",
    country: "",
    deliveryMethod: "delivery",
    agreed: false,
  });

  const currencySymbols = {
    INR: "₹",
    USD: "$",
    EUR: "€",
    GBP: "£",
    AED: "د.إ",
  };

  const convertPrice = (inr) => {
    if (loading) return "...";
    const rate = rates[currency] || 1;
    return (inr * rate).toFixed(2);
  };

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === "checkbox" ? checked : value;

    setForm((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    if (name === "zip" && value.length === 6) {
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${value}`);
        const data = await res.json();
        const postOffice = data[0]?.PostOffice?.[0];

        if (postOffice) {
          setForm((prev) => ({
            ...prev,
            city: postOffice.District,
            state: postOffice.State,
            country: postOffice.Country,
            zip: value,
          }));
        }
      } catch (error) {
        console.error("Invalid ZIP or failed to fetch location data.");
      }
    }
  };

  const calculateSubtotal = () =>
    cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

  const calculateDiscount = () => {
    const discount = localStorage.getItem("applied_coupon_discount");
    return discount ? parseFloat(discount) : 0;
  };

  const getAppliedCouponCode = () =>
    localStorage.getItem("applied_coupon_code") || "";

  const getAppliedCouponCurrency = () =>
    localStorage.getItem("applied_coupon_currency") || "";

  const calculateTotal = () =>
    calculateSubtotal() - calculateDiscount() + getDeliveryFee();

  const handleSubmit = async () => {
    const {
      fullName,
      email,
      phone,
      address,
      zip,
      city,
      state,
      country,
      agreed,
    } = form;

    if (!fullName || !email || !phone || !address || !zip || !city || !state || !country) {
      alert("Please fill out all the fields.");
      return;
    }

    if (!agreed) {
      alert("You must agree to the terms and conditions.");
      return;
    }

    const rate = rates[currency] || 1;

    // Values in INR
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const deliveryConverted = getDeliveryFee(); // in selected currency
    const delivery = deliveryConverted / rate;  // convert back to INR
    const total = subtotal - discount + delivery;

    // Converted values
    const subtotalConverted = subtotal * rate;
    const discountConverted = discount * rate;
    const totalConverted = subtotalConverted - discountConverted + deliveryConverted;

    const couponCode = getAppliedCouponCode();
    const couponCurrency = getAppliedCouponCurrency();

    const orderData = {
      ...form,
      cart,
      currency,
      userId: user?._id || "guest",
      couponCode,
      couponCurrency,

      // Stored in INR
      subtotal,
      discount,
      delivery,
      total,

      // Stored in selected currency
      subtotalConverted,
      discountConverted,
      deliveryConverted,
      totalConverted,
    };

    try {
      await api.post("/api/orders", orderData);
      localStorage.setItem("nutrafox_order", JSON.stringify(orderData));

      // Clear coupon data after successful order
      localStorage.removeItem("applied_coupon_discount");
      localStorage.removeItem("applied_coupon_code");
      localStorage.removeItem("applied_coupon_currency");

      navigate("/order-confirmation");
    } catch (error) {
      console.error("🔥 Failed to save order:", error.message);
      alert("Something went wrong while saving your order.");
    }
  };

  if (loading) return <div className="text-center py-10">Loading prices...</div>;

  return (
    <div className="bg-[#f3f0e4] min-h-screen px-4 sm:px-10 py-10 text-[#105132]">
      <h1 className="text-3xl font-bold mb-10 text-center">Checkout</h1>

      <div className="flex flex-col lg:flex-row gap-10">
        <div className="w-full lg:w-2/3 bg-white p-6 rounded-xl shadow-md">
          <h2 className="text-xl font-semibold mb-6">Shipping Information</h2>

          <div className="flex gap-4 mb-6">
            <button
              className={`flex-1 border p-2 rounded-md ${form.deliveryMethod === "delivery" ? "bg-[#105132] text-white" : ""}`}
              onClick={() => setForm((f) => ({ ...f, deliveryMethod: "delivery" }))}
            >
              🚚 Delivery
            </button>
            <button
              className={`flex-1 border p-2 rounded-md ${form.deliveryMethod === "pickup" ? "bg-[#105132] text-white" : ""}`}
              onClick={() => setForm((f) => ({ ...f, deliveryMethod: "pickup" }))}
            >
              📦 Pick up
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input name="fullName" placeholder="Full name" className="border p-2 rounded-md" onChange={handleChange} />
            <input name="email" type="email" placeholder="Email address" className="border p-2 rounded-md" onChange={handleChange} />
            <input name="phone" type="tel" placeholder="Phone number" className="border p-2 rounded-md" onChange={handleChange} />
            <input name="address" placeholder="Address Line" className="border p-2 rounded-md" onChange={handleChange} />
            <input name="zip" placeholder="ZIP code" className="border p-2 rounded-md" onChange={handleChange} />
            <input name="city" placeholder="City" className="border p-2 rounded-md" value={form.city} readOnly />
            <input name="state" placeholder="State" className="border p-2 rounded-md" value={form.state} readOnly />
            <input name="country" placeholder="Country" className="border p-2 rounded-md" value={form.country} readOnly />
          </div>

          <label className="flex items-center gap-2 mt-4">
            <input type="checkbox" name="agreed" onChange={handleChange} />
            <span>I have read and agree to the Terms and Conditions.</span>
          </label>
        </div>

        <div className="w-full lg:w-1/3 bg-white p-6 rounded-xl shadow-md">
          <h2 className="text-xl font-semibold mb-6">Review your cart</h2>

          <div className="space-y-4 mb-6">
            {cart.map((item, idx) => (
              <div key={idx} className="flex justify-between">
                <span>{item.name} x {item.quantity}</span>
                <span>{currencySymbols[currency]}{convertPrice(item.price * item.quantity)} {currency}</span>
              </div>
            ))}
          </div>

          <div className="border-t pt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{currencySymbols[currency]}{convertPrice(calculateSubtotal())} {currency}</span>
            </div>
            <div className="flex justify-between text-[#c0a230]">
              <span>Discount</span>
              <span>- {currencySymbols[currency]}{convertPrice(calculateDiscount())} {currency}</span>
            </div>
            <div className="flex justify-between">
              <span>Delivery</span>
              <span>{currencySymbols[currency]}{convertPrice(getDeliveryFee())} {currency}</span>
            </div>
            <hr />
            <div className="flex justify-between font-bold text-base pt-2">
              <span>Total</span>
              <span>{currencySymbols[currency]}{convertPrice(calculateTotal())} {currency}</span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className="w-full mt-6 py-3 bg-[#105132] text-white font-semibold rounded-xl hover:bg-[#236e47] transition-all"
          >
            Pay Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
